import React, {createContext, ReactNode, useCallback, useEffect, useReducer} from 'react'
import {ActionMap, AuthState, JWTContextType} from '../@types/auth'
import {isValidToken, setSession} from '../utils/jwt'

enum Types {
  Initial = 'INITIALIZE',
  Logout = 'LOGOUT'
}

type JWTAuthPayload = {
  [Types.Initial]: {
    isAuthenticated: boolean
  }
  [Types.Logout]: undefined
}

export type JWTActions = ActionMap<JWTAuthPayload>[keyof ActionMap<JWTAuthPayload>]

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false
}

const JWTReducer = (state: AuthState, action: JWTActions) => {
  switch (action.type) {
    case 'INITIALIZE':
      return {
        isAuthenticated: action.payload.isAuthenticated,
        isInitialized: true
      }
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false
      }
    default:
      return state
  }
}

const AuthContext = createContext<JWTContextType | null>(null)

type AuthProviderProps = {
  children: ReactNode
}

function AuthProvider({children}: AuthProviderProps) {
  const [state, dispatch] = useReducer(JWTReducer, initialState)

  useEffect(() => {
    const initialize = () => {
      const accessToken = localStorage.getItem('accessToken')
      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken)
        dispatch({
          type: Types.Initial,
          payload: {
            isAuthenticated: true
          }
        })
      } else {
        dispatch({
          type: Types.Initial,
          payload: {
            isAuthenticated: false
          }
        })
      }
    }

    initialize()
  }, [])

  const logout = useCallback(() => {
    setSession(null)
    dispatch({type: Types.Logout})
  }, [])

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export {AuthContext, AuthProvider}
